// @ts-nocheck

import { createApi } from '@reduxjs/toolkit/query/react';

import {
  APPLY_PROMO_CODE_FAILURE,
  APPLY_PROMO_CODE_REQUEST,
  APPLY_PROMO_CODE_SUCCESS,
  REMOVE_PROMO_CODE_FAILURE,
  REMOVE_PROMO_CODE_REQUEST,
  REMOVE_PROMO_CODE_SUCCESS,
  DELETE_BASKET_ITEMS_FAILURE,
  DELETE_BASKET_ITEMS_REQUEST,
  DELETE_BASKET_ITEMS_SUCCESS,
  deleteExpiredGwpItems,
  deleteForbiddenUnlockedXclusiveProducts,
  UPDATE_ITEM_QUANTITY_FAILURE,
  UPDATE_ITEM_QUANTITY_REQUEST,
  UPDATE_ITEM_QUANTITY_SUCCESS,
} from '../actions';

import {
  fetchBentoQuery,
  handleLegacyReduxActions,
  withBentoApi,
} from './utils';

interface CartCountResponse {
  cartId: string;
  itemCount: number;
}

type ChangeProductsFromCartArgs = {
  kind: 'productSet' | 'productById';
  productGroupKey: string;
};

type DeleteProductsFromCartArgs = ChangeProductsFromCartArgs;

type UpdateProductsQuantityInCartArgs = ChangeProductsFromCartArgs & {
  quantity: number;
};
type ApplyPromoCodeArgs = {
  promoCode: string;
};
type RemovePromoCodeArgs = ApplyPromoCodeArgs;

const getRequestPath = ({
  kind,
  productGroupKey,
}: ChangeProductsFromCartArgs) => {
  switch (kind) {
    case 'productSet':
      return `cart/sets/${productGroupKey}`;
    case 'productById':
      return `cart/products/${productGroupKey}`;
  }
};

const UPDATE_CART_DRAWER_ITEM_QUANTITY_ACTIONS = [
  UPDATE_ITEM_QUANTITY_REQUEST,
  UPDATE_ITEM_QUANTITY_SUCCESS,
  UPDATE_ITEM_QUANTITY_FAILURE,
];
const APPLY_PROMO_ACTIONS = [
  APPLY_PROMO_CODE_REQUEST,
  APPLY_PROMO_CODE_SUCCESS,
  APPLY_PROMO_CODE_FAILURE,
];
const REMOVE_PROMO_ACTIONS = [
  REMOVE_PROMO_CODE_REQUEST,
  REMOVE_PROMO_CODE_SUCCESS,
  REMOVE_PROMO_CODE_FAILURE,
];
export const cartApi = createApi({
  reducerPath: 'cartApi',
  baseQuery: fetchBentoQuery(),

  endpoints: (builder) => ({
    getCartItems: builder.query({
      queryFn: withBentoApi(async (args, bentoApi) => {
        try {
          const res = await bentoApi(`/cart?timestamp=${Date.now()}`, args);
          const data = await res.json();
          return { data };
        } catch (error) {
          return { error };
        }
      }),
    }),

    deleteCartDrawerItem: builder.mutation<void, DeleteProductsFromCartArgs>({
      queryFn: withBentoApi(async (args, bentoApi, { dispatch }) => {
        const { kind, productGroupKey } = args;

        dispatch({ type: DELETE_BASKET_ITEMS_REQUEST });

        let response;
        try {
          switch (kind) {
            case 'productSet':
              response = await bentoApi(`/cart/sets/${productGroupKey}`, {
                method: 'DELETE',
              });
              break;
            case 'productById':
              response = await bentoApi(`/cart/products/${productGroupKey}`, {
                method: 'DELETE',
              });
              break;
          }
          const payload = await response.json();

          await Promise.allSettled([
            dispatch(deleteExpiredGwpItems()),
            dispatch(deleteForbiddenUnlockedXclusiveProducts()),
          ]);
          dispatch({
            type: DELETE_BASKET_ITEMS_SUCCESS,
            payload,
            meta: {
              fetchedDate: new Date().toISOString(),
            },
          });
          return { data: payload };
        } catch (error) {
          dispatch({ type: DELETE_BASKET_ITEMS_FAILURE });
          return { error };
        }
      }),

      getCartItemCount: builder.query<CartCountResponse, void>({
        queryFn: withBentoApi(async (_, bentoApi) => {
          try {
            const res = await bentoApi(
              `/cart/items/count?timestamp=${Date.now()}`
            );
            const data = await res.json();
            return { data: data as CartCountResponse };
          } catch (error) {
            return { error };
          }
        }),
      }),
    }),

    updateCartDrawerItemQuantity: builder.mutation<
      void,
      UpdateProductsQuantityInCartArgs
    >({
      queryFn: withBentoApi(
        async ({ kind, productGroupKey, quantity }, bentoApi) => {
          const path = getRequestPath({ kind, productGroupKey });
          try {
            const res = await bentoApi(`${path}/quantity`, {
              method: 'POST',
              body: JSON.stringify({ quantity }),
            });
            const data = await res.json();
            return { data };
          } catch (error) {
            return { error };
          }
        }
      ),
      onQueryStarted: handleLegacyReduxActions(
        UPDATE_CART_DRAWER_ITEM_QUANTITY_ACTIONS
      ),
    }),

    applyPromoCode: builder.mutation<void, ApplyPromoCodeArgs>({
      queryFn: withBentoApi(async ({ promoCode }, bentoApi) => {
        try {
          const res = await bentoApi(`cart/promocode`, {
            method: 'POST',
            body: JSON.stringify({ promoCode }),
          });
          const data = await res.json();
          return { data };
        } catch (error) {
          return { error };
        }
      }),
      onQueryStarted: handleLegacyReduxActions(APPLY_PROMO_ACTIONS),
    }),

    removePromoCode: builder.mutation<void, RemovePromoCodeArgs>({
      queryFn: withBentoApi(async ({ promoCode }, bentoApi) => {
        try {
          const res = await bentoApi(`cart/promocode/${promoCode}`, {
            method: 'DELETE',
          });
          const data = await res.json();
          return { data };
        } catch (error) {
          return { error };
        }
      }),
      onQueryStarted: handleLegacyReduxActions(REMOVE_PROMO_ACTIONS),
    }),
  }),
});

export const {
  useDeleteCartDrawerItemMutation,
  useUpdateCartDrawerItemQuantityMutation,
  useApplyPromoCodeMutation,
  useRemovePromoCodeMutation,
} = cartApi;
